<div class="onboarding-dialog">
  <h2 class="onboarding-dialog__title">
    {{ getStepTitle(currentStep) | translate }}
  </h2>

  <form [formGroup]="preferencesForm">
    <mat-dialog-content class="onboarding-dialog__content">
      <ng-container [ngSwitch]="currentStep">

        <!-- Step 1 -->
        <div *ngSwitchCase="1" class="onboarding-dialog__step onboarding-dialog__welcome">
          <app-icon name="heart-outline" [size]="IconSize.Large"></app-icon>
          <p>{{ 'onboarding.step1.description' | translate }}</p>
        </div>

        <!-- Step 2 -->
        <div *ngSwitchCase="2" class="onboarding-dialog__step">
          <label>{{ 'onboarding.language.label' | translate }}</label>
          <mat-form-field appearance="fill">
            <mat-select formControlName="preferred_language">
              <mat-option *ngFor="let lang of languageOptions" [value]="lang.value">
                {{ 'onboarding.language.' + lang.value | translate }}
              </mat-option>
            </mat-select>
          </mat-form-field>
        </div>

        <!-- Step 3 -->
        <div *ngSwitchCase="3" class="onboarding-dialog__step">
          <label class="onboarding-dialog__label">{{ 'onboarding.name.label' | translate }}</label>
          <mat-form-field appearance="fill" class="onboarding-dialog__name-input">
            <input matInput [placeholder]="'onboarding.name.placeholder' | translate" formControlName="name" />
            <mat-hint align="end">{{ preferencesForm.get('name')?.value?.length || 0 }}/100</mat-hint>
            <mat-error *ngIf="preferencesForm.get('name')?.invalid && preferencesForm.get('name')?.touched">
              {{ 'onboarding.name.error' | translate }}
            </mat-error>
          </mat-form-field>
        </div>

        <!-- Step 4 -->
        <div *ngSwitchCase="4" class="onboarding-dialog__step">
          <label>{{ 'onboarding.unit.label' | translate }}</label>
          <mat-radio-group formControlName="preferred_unit_system">
            <mat-radio-button *ngFor="let unit of units" [value]="unit">
              {{ 'enums.unitSystem.' + unit | translate }}
            </mat-radio-button>
          </mat-radio-group>
        </div>

        <!-- Step 5: Quick household essentials -->
        <div *ngSwitchCase="5" class="onboarding-dialog__step onboarding-dialog__storage-step">
          <div class="onboarding-dialog__storage-header">
            <p>{{ 'storageQuickAdd.pickSubtitle' | translate }}</p>
            <p *ngIf="quotaEnforced" class="onboarding-dialog__storage-limit">
              {{ 'storageQuickAdd.quota' | translate:{ left: (maxSelectable - selectedCount) } }}
            </p>

            <div class="onboarding-dialog__storage-warning" *ngIf="selectedCount < 5">
              <app-icon name="alert-circle" class="onboarding-dialog__storage-warning-icon"></app-icon>
              <span>{{ 'onboarding.storage.minPickWarning' | translate:{ min: 5 } }}</span>
            </div>
          </div>

          <ng-container *ngIf="(essentialsLoading$ | async); else storageEssentials">
            <div class="onboarding-dialog__center"><app-spinner></app-spinner></div>
          </ng-container>

          <ng-template #storageEssentials>
            <div class="onboarding-dialog__chips">
              <button
                class="onboarding-dialog__chip"
                [class.onboarding-dialog__chip--active]="isStorageSelected(item.ingredient_id)"
                type="button"
                *ngFor="let item of (essentials$ | async)"
                (click)="toggleStorageSelection(item.ingredient_id)">
                <app-icon [name]="item.icon || 'food-variant'"></app-icon>
                <span>{{ item.displayName || item.name || item.hu_name }}</span>
              </button>
            </div>
          </ng-template>
        </div>

        <!-- Step 6: Quantity and unit check -->
        <div *ngSwitchCase="6" class="onboarding-dialog__step onboarding-dialog__storage-step">
          <p class="onboarding-dialog__storage-note">{{ 'storageQuickAdd.autoExpiryNote' | translate }}</p>

          <div [formGroup]="storageItemsForm" class="onboarding-dialog__storage-form">
            <div formArrayName="items" class="onboarding-dialog__storage-list">
              <div class="onboarding-dialog__storage-row" *ngFor="let fg of itemsFA.controls; let i = index" [formGroupName]="i">
                <div class="onboarding-dialog__storage-row-title">
                  <app-icon [name]="fg.get('icon')?.value || 'food-variant'"></app-icon>
                  <span>{{ fg.get('name')?.value }}</span>
                </div>

                <div class="onboarding-dialog__storage-fields">
                  <mat-form-field appearance="fill" class="onboarding-dialog__storage-field">
                    <mat-label>{{ 'storageDialog.quantity' | translate }}</mat-label>
                    <input matInput type="number" min="0.01" step="0.01" formControlName="quantity" />
                  </mat-form-field>

                  <mat-form-field appearance="fill" class="onboarding-dialog__storage-field">
                    <mat-label>{{ 'storageDialog.unit' | translate }}</mat-label>
                    <mat-select formControlName="unit">
                      <mat-option *ngFor="let u of selectedUnitsForStorageStep" [value]="u">
                        {{ getStorageUnitLabel(u) }}
                      </mat-option>
                    </mat-select>
                  </mat-form-field>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Steps 7–11 -->
        <div *ngSwitchCase="7" class="onboarding-dialog__step">
          <app-icon name="lightbulb-on-outline" [size]="IconSize.Large"></app-icon>
          <p>{{ 'onboarding.step7.description' | translate }}</p>
        </div>

        <div *ngSwitchCase="8" class="onboarding-dialog__step">
          <app-icon name="fridge-outline" [size]="IconSize.Large"></app-icon>
          <p>{{ 'onboarding.step8.description' | translate }}</p>
        </div>

        <div *ngSwitchCase="9" class="onboarding-dialog__step">
          <app-icon name="cart-outline" [size]="IconSize.Large"></app-icon>
          <p>{{ 'onboarding.step9.description' | translate }}</p>
        </div>

        <div *ngSwitchCase="10" class="onboarding-dialog__step">
          <app-icon name="book-open-page-variant-outline" [size]="IconSize.Large"></app-icon>
          <p>{{ 'onboarding.step10.description' | translate }}</p>
        </div>

        <div *ngSwitchCase="11" class="onboarding-dialog__step">
          <app-icon name="calendar-clock-outline" [size]="IconSize.Large"></app-icon>
          <p>{{ 'onboarding.step11.description' | translate }}</p>
        </div>

        <!-- Final Step -->
        <div *ngSwitchCase="12" class="onboarding-dialog__step onboarding-dialog__celebration">
          <app-icon name="party-popper" [size]="IconSize.Large"></app-icon>
          <p>{{ 'onboarding.step12.description' | translate }}</p>
        </div>

      </ng-container>
    </mat-dialog-content>
  </form>
</div>

<div class="onboarding-dialog__actions">
  <app-button [skin]="ButtonSkin.Secondary" [size]="ButtonSize.Medium" (click)="prevStep()" [isDisabled]="currentStep === 1">
    {{ 'onboarding.back' | translate }}
  </app-button>
  <app-button [skin]="ButtonSkin.Primary" [size]="ButtonSize.Medium" (click)="nextStep()"
    [isDisabled]="
      (currentStep === 2 && preferencesForm.get('preferred_language')?.invalid) ||
      (currentStep === 3 && preferencesForm.get('name')?.invalid) ||
      (currentStep === 5 && !canProceedStorageSelection) ||
      (currentStep === 6 && storageItemsForm.invalid)">
    {{ currentStep === maxStep ? ('onboarding.finish' | translate) : ('onboarding.next' | translate) }}
  </app-button>
</div>
